import { Container, Graphics, Rectangle, Text } from 'pixi.js';
import { PieceType, PlayerSide } from '../types/game.types';
import type { PieceData } from '../types/game.types';
import { clamp, lerp } from '../utils/animation';
import { FONT_UI, PALETTE } from '../ui/theme';

export const TILE_SIZE = 80;
const EMOJI_FONT_SIZE = 44;

export class Piece extends Container {
  private chrome: Graphics;
  private emojiText: Text;
  private time: number = Math.random() * 10;
  readonly pieceData: PieceData;

  private _isSelected: boolean = false;
  private _isHovered: boolean = false;

  constructor(data: PieceData) {
    super();
    this.pieceData = data;

    this.chrome = new Graphics();
    this.addChild(this.chrome);

    const emoji = data.type === PieceType.KIWI ? '🥝' : '🥥';
    this.emojiText = new Text(emoji, {
      fontFamily: FONT_UI,
      fontSize: EMOJI_FONT_SIZE,
    });
    this.emojiText.anchor.set(0.5);
    this.emojiText.x = TILE_SIZE / 2;
    this.emojiText.y = TILE_SIZE / 2 - 2;
    this.addChild(this.emojiText);

    this.renderToken();

    this.eventMode = 'static';
    this.hitArea = new Rectangle(0, 0, TILE_SIZE, TILE_SIZE);
    this.cursor = 'pointer';
    this.on('pointerover', this.onPointerOver);
    this.on('pointerout', this.onPointerOut);
  }

  get side(): PlayerSide {
    return this.pieceData.side;
  }

  get isSelected(): boolean {
    return this._isSelected;
  }

  set isSelected(value: boolean) {
    this._isSelected = value;
    this.renderToken();
  }

  private onPointerOver = (): void => {
    this._isHovered = true;
    this.renderToken();
  };

  private onPointerOut = (): void => {
    this._isHovered = false;
    this.renderToken();
  };

  private renderToken(): void {
    const isKiwi = this.pieceData.side === PlayerSide.KIWI;
    const fillColor = isKiwi ? PALETTE.kiwiDark : PALETTE.coconutDark;
    const glowColor = isKiwi ? PALETTE.kiwi : PALETTE.coconut;
    const innerColor = isKiwi ? PALETTE.kiwi : PALETTE.coconut;

    this.chrome.clear();
    this.chrome.beginFill(0x000000, 0.18);
    this.chrome.drawRoundedRect(TILE_SIZE / 2 - 22, TILE_SIZE / 2 + 13, 44, 12, 6);
    this.chrome.endFill();

    this.chrome.beginFill(fillColor, 0.95);
    this.chrome.drawCircle(TILE_SIZE / 2, TILE_SIZE / 2, 25);
    this.chrome.endFill();

    this.chrome.beginFill(innerColor, 0.2);
    this.chrome.drawCircle(TILE_SIZE / 2, TILE_SIZE / 2 - 4, 18);
    this.chrome.endFill();

    if (this._isSelected) {
      this.chrome.lineStyle(3, PALETTE.accent, 0.95);
      this.chrome.drawCircle(TILE_SIZE / 2, TILE_SIZE / 2, 31);
    } else if (this._isHovered) {
      this.chrome.lineStyle(2, glowColor, 0.7);
      this.chrome.drawCircle(TILE_SIZE / 2, TILE_SIZE / 2, 30);
    }
  }

  animate(delta: number): void {
    this.time += delta;

    const hoverWeight = this._isHovered ? 1 : 0;
    const selectedWeight = this._isSelected ? 1 : 0;
    const pulse = selectedWeight * (Math.sin(this.time * 7) * 0.5 + 0.5);
    const targetScale = 1 + hoverWeight * 0.04 + selectedWeight * 0.05 + pulse * 0.02;
    const targetYOffset = -hoverWeight * 3 - selectedWeight * 4 - pulse * 1.5;
    const targetXOffset = (TILE_SIZE / 2) * (1 - targetScale);

    this.chrome.scale.set(lerp(this.chrome.scale.x, targetScale, Math.min(delta * 12, 1)));
    this.chrome.alpha = clamp(0.82 + pulse * 0.18 + hoverWeight * 0.08, 0, 1);
    this.chrome.x = lerp(this.chrome.x, targetXOffset, Math.min(delta * 12, 1));
    this.chrome.y = lerp(
      this.chrome.y,
      targetYOffset + (TILE_SIZE / 2) * (1 - targetScale),
      Math.min(delta * 12, 1),
    );
    this.emojiText.y = lerp(
      this.emojiText.y,
      TILE_SIZE / 2 - 2 + targetYOffset,
      Math.min(delta * 12, 1),
    );
  }

  flashCapture(): Promise<void> {
    return new Promise((resolve) => {
      let elapsed = 0;
      const duration = 300;
      const tick = (): void => {
        elapsed += 16;
        this.alpha = elapsed % 100 < 50 ? 1 : 0.3;
        if (elapsed >= duration) {
          this.alpha = 0;
          resolve();
          return;
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  }
}
